import { Reference } from "@apollo/client";
import { IonCol, IonGrid, IonReorderGroup, IonRow, ItemReorderEventDetail } from "@ionic/react";
import { MouseEvent, useCallback, useContext, useEffect, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { formatPhoneNumber } from "react-phone-number-input";
import { useHistory, useLocation } from "react-router";
import { createEnumParam, useQueryParams, withDefault } from "use-query-params";

import Button from "@components/buttons/Button";
import CircleButton from "@components/buttons/CircleButton";
import DirectionButton from "@components/buttons/DirectionButton";
import Segment from "@components/buttons/Segment";
import CardBase from "@components/cards/CardBase";
import HeroCardWorkingOnIt from "@components/cards/HeroCardWorkingOnIt";
import ClientVisibleTaskResource from "@components/ClientVisibleTaskResource";
import ClientVisibleTaskResourceActions from "@components/ClientVisibleTaskResourceActions";
import ClientVisibleTaskResourceCard from "@components/ClientVisibleTaskResourceCard";
import styles from "@components/ClientVisibleTaskResources.module.css";
import ClientVisibleTaskResourceUsefulnessButtons from "@components/ClientVisibleTaskResourceUsefulnessButtons";
import DeleteConfirmationAlert from "@components/DeleteConfirmationAlert";
import ResourceForm from "@components/forms/ResourceForm";
import TaskResourceFeaturesForm from "@components/forms/TaskResourceFeaturesForm";
import HotlineLink from "@components/HotlineLink";
import MobileHeader from "@components/MobileHeader";
import Modal from "@components/Modal";
import PinToggle from "@components/PinToggle";
import Icon from "@components/text/Icon";
import IconBase from "@components/text/IconBase";
import BodyCopy from "@components/typography/BodyCopy";
import ExternalLink from "@components/typography/ExternalLink";
import Heading1 from "@components/typography/Heading1";
import { openerContext } from "@context/Contexts";
import useGlobalOpener from "@hooks/useGlobalOpener";
import useLazyQueryWithPromise from "@hooks/useLazyQueryWithPromise";
import useMutationWithValidation from "@hooks/useMutationWithValidation";
import useMyClient from "@hooks/useMyClient";
import useMyExpert from "@hooks/useMyExpert";
import useOpener from "@hooks/useOpener";
import { OpenerTypeEnum } from "@typing/Enums";
import {
  ClientRoleRoleEnum,
  GetJourneyWithPlanQuery,
  GetPdfDownloadDocument,
  GetPdfDownloadQuery,
  GetPdfDownloadQueryVariables,
  GoalStatusEnum,
  PdfTypeEnum,
  PinResourceTypeEnum,
  ResourceDestroyDocument,
  ResourceDestroyMutation,
  ResourceDestroyMutationVariables,
  ResourceFragment,
  ResourcesReorderDocument,
  ResourcesReorderMutation,
  ResourcesReorderMutationVariables,
  ResourceUpdateDocument,
  ResourceUpdateMutation,
  ResourceUpdateMutationVariables,
  TaskFragment,
  TaskMarkReadDocument,
  TaskMarkReadMutation,
  TaskMarkReadMutationVariables
} from "@typing/Generated";
import { downloadFile } from "@utils/downloadUtils";
import { moveArrayByIndex, resourceHasChanges, sortByPosition, sortByTitle } from "@utils/modelUtils";

type Props = {
  journey: GetJourneyWithPlanQuery["journey"];
  task: TaskFragment;
};

enum ResourceViewType {
  CHART = "CHART",
  LIST = "LIST"
}

const ClientVisibleTaskResources = ({ journey, task }: Props) => {
  const myClient = useMyClient();
  const myExpert = useMyExpert();
  const history = useHistory();
  const location = useLocation();
  const intl = useIntl();
  const searchParams = new URLSearchParams(location.search);
  const focusResourceId = searchParams.get("resource-id");

  const resourceRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  const getPdfDownload = useLazyQueryWithPromise<GetPdfDownloadQuery, GetPdfDownloadQueryVariables>(
    GetPdfDownloadDocument
  );

  const handleDownload = useCallback(
    (event: MouseEvent<HTMLIonButtonElement>) => {
      event.preventDefault();
      getPdfDownload({
        fetchPolicy: "no-cache",
        variables: { id: task.id, journeyId: journey.id, type: PdfTypeEnum.TASK_RESOURCES }
      }).then(response => {
        downloadFile(response.data.pdfDownload.url, "grayce-resources.pdf");
      });
    },
    [getPdfDownload, journey.id, task.id]
  );

  useEffect(() => {
    let timeout: NodeJS.Timeout | null = null;

    if (focusResourceId) {
      timeout = setTimeout(() => {
        const element = resourceRefs.current[focusResourceId];
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
        }
      }, 700);
    }

    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [focusResourceId]);

  const { toggleOpener } = useContext(openerContext);

  const resourceOpener = useOpener(myExpert ? true : false);
  const resourceDeleteOpener = useOpener();
  const resourceFeaturesOpener = useOpener();
  const timeTrackingOpener = useGlobalOpener(OpenerTypeEnum.TIME_LOG);

  const [resourceBeingEdited, setResourceBeingEdited] = useState<ResourceFragment | undefined>(undefined);
  const [read, setRead] = useState(false);

  const [markTaskRead] = useMutationWithValidation<TaskMarkReadMutation, TaskMarkReadMutationVariables>(
    TaskMarkReadDocument,
    "task"
  );

  const [destroyResource] = useMutationWithValidation<ResourceDestroyMutation, ResourceDestroyMutationVariables>(
    ResourceDestroyDocument,
    "resource",
    {
      update: (store, { data }) => {
        if (data?.resourceDestroy?.id) {
          store.modify({
            fields: {
              resources(cachedRefs: readonly Reference[], { readField }) {
                return cachedRefs.filter(ref => data.resourceDestroy?.id !== readField("id", ref));
              }
            },
            id: store.identify({ ...task })
          });
        }
      }
    }
  );

  useEffect(() => {
    if (!read && myClient?.id === journey.clientRoles.find(role => role.role === ClientRoleRoleEnum.OWNER)?.client.id) {
      markTaskRead({
        id: task.id
      });
      setRead(true);
    }
  }, [journey.clientRoles, journey.id, markTaskRead, myClient?.id, read, task.id]);

  const handleDestroy = useCallback(
    (resource: ResourceFragment) => {
      setResourceBeingEdited(resource);
      resourceDeleteOpener.open();
    },
    [resourceDeleteOpener]
  );

  const handleCreate = useCallback(() => {
    setResourceBeingEdited(undefined);
    resourceOpener.open();
  }, [resourceOpener]);

  const handleResourceSuccess = useCallback(
    (editing: boolean) => {
      setResourceBeingEdited(undefined);
      if (!editing && journey.carePlanPublishedAt) {
        timeTrackingOpener.open();
      }
    },
    [journey.carePlanPublishedAt, timeTrackingOpener]
  );

  const handleEdit = useCallback(
    (resource: ResourceFragment) => {
      setResourceBeingEdited(resource);
      resourceOpener.open();
    },
    [resourceOpener]
  );

  const handleResourceDestroy = useCallback(() => {
    if (resourceBeingEdited) {
      destroyResource({
        id: resourceBeingEdited.id
      }).then(() => {
        setResourceBeingEdited(undefined);
        resourceOpener.close();
      });
    }
  }, [destroyResource, resourceBeingEdited, resourceOpener]);

  const sortedResources = sortByPosition(task.resources).filter(resource => resource.useful !== false);
  const sortedUnusefulResources = sortByTitle(task.resources.filter(resource => resource.useful === false));
  const anyResources = sortedResources.length > 0 || sortedUnusefulResources.length > 0;

  const generateHandleDelete = useCallback(
    (resource: ResourceFragment) => () => {
      handleDestroy(resource);
    },
    [handleDestroy]
  );

  const generateHandleEdit = useCallback(
    (resource: ResourceFragment) => () => {
      handleEdit(resource);
    },
    [handleEdit]
  );

  const [updateResource] = useMutationWithValidation<ResourceUpdateMutation, ResourceUpdateMutationVariables>(
    ResourceUpdateDocument,
    "resource"
  );

  const updateUseful = useCallback(
    (resource: ResourceFragment, useful: boolean) => {
      updateResource({
        id: resource.id,
        useful
      });
    },
    [updateResource]
  );

  const activeGoal = journey.goals?.find(goal => goal.id === task.goal?.activeGoalId);
  const resourceContainsChanges = (r: ResourceFragment) =>
    resourceHasChanges(r, activeGoal) && task.goal?.status === GoalStatusEnum.EDIT_MODE;

  const viewButtons = [
    {
      maxWidth: 130,
      textKey: "components.CarePlan.clientVisibleTask.details",
      value: ResourceViewType.LIST
    },
    {
      maxWidth: 130,
      textKey: "components.CarePlan.clientVisibleTask.compare",
      value: ResourceViewType.CHART
    }
  ];

  const viewParam = createEnumParam<ResourceViewType>(Object.values(ResourceViewType));

  const [query, setQuery] = useQueryParams({
    section: withDefault(viewParam, ResourceViewType.LIST)
  });

  const [view, setView] = useState<ResourceViewType>(query.section);

  useEffect(() => {
    if (query.section !== view) setView(query.section);
  }, [setQuery, query.section, view]);

  const chartValueWrapperStyles = (r: ResourceFragment) =>
    `${styles.chartValueWrapper} ${resourceContainsChanges(r) ? styles.changesSides : ""}`;

  const [resourcesReorder] = useMutationWithValidation<ResourcesReorderMutation, ResourcesReorderMutationVariables>(
    ResourcesReorderDocument,
    "resource"
  );

  const reorderResources = useCallback(
    (event: CustomEvent<ItemReorderEventDetail>) => {
      const newResourcesOrderIds = moveArrayByIndex(
        event,
        task.resources.filter(resource => resource.useful !== false)
      ).map(resource => resource.id);
      resourcesReorder({
        resourceIds: newResourcesOrderIds,
        taskId: task.id
      }).then(() => {
        event.detail.complete();
      });
    },
    [task.resources, task.id, resourcesReorder]
  );

  return (
    <div>
      <div className="flex justify-between ion-hide-lg-down">
        <div>
          <DirectionButton direction="back" />
        </div>
        <div>
          {anyResources && (
            <Button fill="clear" onClick={handleDownload}>
              <IconBase color="primary" icon="download" slot="start" />
              <BodyCopy bold color="primary" size="small">
                <FormattedMessage id="components.CarePlan.clientVisibleTask.download" />
              </BodyCopy>
            </Button>
          )}
          {myExpert && (
            <div className={`do-not-print ${styles.featuresButton}`}>
              <Button fill="clear" onClick={resourceFeaturesOpener.open}>
                <IconBase icon="screwdriverWrench" slot="start" />
                <FormattedMessage id="pages.carePlan.task.resources.editFeatureCriteria" />
              </Button>
            </div>
          )}
        </div>
      </div>
      <MobileHeader handleBack={() => history.goBack()} title={task.title} />
      <div className={styles.content}>
        <IonGrid className="ion-no-padding ion-hide-lg-down">
          <IonRow className="ion-align-items-center">
            <IonCol className="margin-20-right">
              <Heading1>{task.title}</Heading1>
            </IonCol>
            <IonCol size="auto">
              {anyResources && (
                <Segment buttons={viewButtons} onChange={v => setQuery({ section: v })} skipSpacing value={view} />
              )}
            </IonCol>
          </IonRow>
        </IonGrid>
        <div className={styles.flexPanes}>
          <div className={myExpert ? styles.flexMainPane : styles.flexMainPaneFull}>
            {anyResources ? (
              <div className={styles.resources}>
                {view === ResourceViewType.LIST ? (
                  <IonReorderGroup
                    className={`${styles.list} ${myExpert ? "full-width" : ""}`}
                    disabled={false}
                    onIonItemReorder={reorderResources}
                  >
                    {sortedResources.map(r => (
                      <ClientVisibleTaskResourceCard
                        generateHandleDelete={generateHandleDelete}
                        generateHandleEdit={generateHandleEdit}
                        journey={journey}
                        key={r.id}
                        resource={r}
                        task={task}
                        updateUseful={updateUseful}
                      />
                    ))}
                  </IonReorderGroup>
                ) : (
                  <div className={styles.resourceChart}>
                    <div className={styles.chartRow}>
                      {sortedResources.map(resource => (
                        <div className={styles.chartColumn} key={resource.id}>
                          <div className={`${chartValueWrapperStyles(resource)} ${styles.chartValueWrapperTop}`}>
                            <div className="full-width flex justify-between">
                              <BodyCopy bold color="primary">
                                {resource.title}
                              </BodyCopy>
                              <div>
                                <PinToggle
                                  color="gray-500"
                                  goal={task.goal}
                                  journeyId={journey.id}
                                  resourceId={resource.id}
                                  resourceType={PinResourceTypeEnum.RESOURCE}
                                />

                                {myExpert && (
                                  <>
                                    <CircleButton
                                      ariaLabelKey="accessibility.ariaLabels.edit"
                                      fill="clear"
                                      icon="edit"
                                      onClick={generateHandleEdit(resource)}
                                      size="medium"
                                    />
                                    <CircleButton
                                      ariaLabelKey="accessibility.ariaLabels.delete"
                                      fill="clear"
                                      icon="trash"
                                      onClick={generateHandleDelete(resource)}
                                      size="medium"
                                    />
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    {sortedResources.some(resource => resource.cost) && (
                      <div className={styles.chartRow}>
                        {sortedResources.map(resource => (
                          <div className={styles.chartColumn} key={resource.id}>
                            <div className={chartValueWrapperStyles(resource)}>
                              {resource.cost && (
                                <div className="flex">
                                  <div className="ion-margin-end">
                                    <Icon color="gray-200" icon="moneyBill" />
                                  </div>
                                  <BodyCopy color="gray" size="small">
                                    {resource.cost}
                                  </BodyCopy>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    {sortedResources.some(resource => resource.phoneNumber) && (
                      <div className={styles.chartRow}>
                        {sortedResources.map(resource => (
                          <div className={styles.chartColumn} key={resource.id}>
                            <div className={chartValueWrapperStyles(resource)}>
                              {resource.phoneNumber && (
                                <div className="flex">
                                  <div className="ion-margin-end margin-4-top">
                                    <Icon color="gray-200" icon="phone" />
                                  </div>

                                  <ExternalLink
                                    href={`tel:${resource.phoneNumber}`}
                                    linkEventCreateVariables={{
                                      journeyId: journey.id,
                                      resourceId: resource.id,
                                      resourceType: resource.__typename
                                    }}
                                  >
                                    {formatPhoneNumber(resource.phoneNumber)
                                      ? formatPhoneNumber(resource.phoneNumber)
                                      : resource.phoneNumber}
                                  </ExternalLink>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    {sortedResources.some(resource => resource.hotline) && (
                      <div className={`${styles.chartRow}`}>
                        {sortedResources.map(resource => (
                          <div className={styles.chartColumn} key={resource.id}>
                            <div className={chartValueWrapperStyles(resource)}>
                              {resource.hotline && (
                                <div className="flex items-center">
                                  <div className="ion-margin-end margin-4-top">
                                    <Icon color="gray-200" icon="phone" />
                                  </div>
                                  <HotlineLink hotline={resource.hotline} journeyId={journey.id} resource={resource} />
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    {sortedResources.some(resource => resource.linkUrl && resource.linkTitle) && (
                      <div className={`${styles.chartRow}`}>
                        {sortedResources.length > 0 ? (
                          <>
                            {sortedResources.map(resource => (
                              <div className={`${styles.chartColumn}`} key={resource.id}>
                                <div className={chartValueWrapperStyles(resource)}>
                                  {resource.linkUrl && resource.linkTitle && (
                                    <div className="flex">
                                      <div className="ion-margin-end margin-4-top">
                                        <Icon color="gray-200" icon="globe" />
                                      </div>
                                      <ExternalLink
                                        href={resource.linkUrl}
                                        linkEventCreateVariables={{
                                          journeyId: journey.id,
                                          resourceId: resource.id,
                                          resourceType: resource.__typename
                                        }}
                                      >
                                        {resource.linkTitle}
                                      </ExternalLink>
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                          </>
                        ) : (
                          <div className={`${styles.chartColumn}`}></div>
                        )}
                      </div>
                    )}
                    {sortByPosition(task.resourceFeatures).map((resourceFeature, index) => (
                      <div className={`${styles.chartRow}`} key={resourceFeature.id}>
                        {sortedResources.length > 0 ? (
                          <>
                            {sortedResources.map(resource => (
                              <ClientVisibleTaskResource
                                chartValueWrapperStyles={chartValueWrapperStyles}
                                index={index}
                                journeyId={journey.id}
                                key={resource.id}
                                resource={resource}
                                resourceFeature={resourceFeature}
                              />
                            ))}
                          </>
                        ) : (
                          <div className={`${styles.chartColumn}`}></div>
                        )}
                      </div>
                    ))}
                    <div className={styles.chartRow}>
                      {sortedResources.map(resource => (
                        <div className={`${styles.chartColumn} ${styles.chartColumnBottom}`} key={resource.id}>
                          <div
                            className={`${chartValueWrapperStyles(resource)} ${styles.chartValueWrapperBottom} ${
                              resourceContainsChanges(resource) ? styles.changesBottom : ""
                            }`}
                          >
                            {!task.goal?.hasDuplicateGoalInEditMode && (
                              <ClientVisibleTaskResourceActions
                                className="margin-10-bottom"
                                resource={resource}
                                updateUseful={updateUseful}
                              />
                            )}
                          </div>
                          <div className={styles.spacing} />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {sortedUnusefulResources.length > 0 && (
                  <div className="margin-20-top">
                    <Heading1>
                      <FormattedMessage id="components.CarePlan.clientVisibleTask.notAMatch" />
                    </Heading1>
                    <div className="flex flex-column gap-md margin-20-top">
                      {sortedUnusefulResources.map(resource => (
                        <CardBase
                          body={
                            <div className="flex items-center">
                              <BodyCopy bold className="flex-grow" color="primary">
                                {resource.title}
                              </BodyCopy>
                              <ClientVisibleTaskResourceUsefulnessButtons
                                resource={resource}
                                updateUseful={updateUseful}
                              />
                            </div>
                          }
                          className={styles.notUsefulCard}
                          key={resource.id}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              // the extra div wrapper puts a buffer between the card and the parent so the card
              // doesn't take up the full height
              <div>
                <HeroCardWorkingOnIt />
              </div>
            )}
          </div>

          {myExpert && (
            <div className={styles.flexEditorPane}>
              <div className={styles.flexEditorPaneContent}>
                {resourceOpener.isOpen ? (
                  <ResourceForm
                    autofocus={false}
                    journey={journey}
                    key={resourceBeingEdited?.id}
                    onCancel={resourceOpener.close}
                    onDelete={resourceDeleteOpener.open}
                    onSuccess={handleResourceSuccess}
                    resource={resourceBeingEdited}
                    task={task}
                  />
                ) : (
                  <Button expand="block" onClick={handleCreate}>
                    <FormattedMessage id="pages.carePlan.task.resources.addResource" />
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      {!myExpert && (
        <div className="ion-margin-top-more align-center ion-padding-start ion-padding-end">
          <Heading1>
            <FormattedMessage id="components.CarePlan.clientVisibleTask.haveFeedback" />
          </Heading1>
          <div className="margin-10-top">
            <BodyCopy color="black">
              <FormattedMessage id="components.CarePlan.clientVisibleTask.haveFeedbackDescription" />
            </BodyCopy>
          </div>
          <div className="margin-20-top">
            <Button onClick={() => toggleOpener(OpenerTypeEnum.MESSAGES, true)}>
              <FormattedMessage id="components.CarePlan.clientVisibleTask.sendMessage" />
              <IconBase color="white" icon="messagesQuestion" slot="end" />
            </Button>
          </div>
        </div>
      )}

      {myExpert && (
        <Modal
          opener={resourceFeaturesOpener}
          title={intl.formatMessage({
            id: "models.tasks.editResourceFeatures"
          })}
        >
          {() => (
            <TaskResourceFeaturesForm
              onCancel={resourceFeaturesOpener.close}
              onSuccess={resourceFeaturesOpener.close}
              task={task}
            />
          )}
        </Modal>
      )}
      <DeleteConfirmationAlert
        messageKey="pages.carePlan.task.resources.reallyDeleteResource"
        messageValue={resourceBeingEdited?.title}
        messageValueKey="title"
        onDelete={handleResourceDestroy}
        opener={resourceDeleteOpener}
      />
    </div>
  );
};

export default ClientVisibleTaskResources;
